import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import Image from 'next/image';
import progressBarLight from '@/public/assets/image/gamification/gamification-progress-light.webp';
import { cn } from '@/utils/cn';

const gmProgressBarVariant = cva('relative rounded-full gm-progress-bar-container', {
  variants: {
    appearance: {
      mobileBar: 'h-3 [&_[data-slot=progressBarFill]]:-top-px [&_[data-slot=progressBarFill]]:h-3.5 [&_[data-slot=progressBarTrack]]:h-3',
      desktopWidget: 'h-4 [&_[data-slot=progressBarFill]]:h-4 [&_[data-slot=progressBarTrack]]:h-[1.0625rem]',
    },
  },
  defaultVariants: {
    appearance: 'mobileBar',
  },
});

interface GmProgressBarProps extends VariantProps<typeof gmProgressBarVariant> {
  className?: string;
  percent: number;
  currentStep?: number;
  totalSteps?: number;
  // TODO: not sure about this prop, waiting for business confirmation
  showLightEffect?: boolean;
}

export const GmProgressBar = ({ className, percent, showLightEffect = true, appearance, currentStep, totalSteps }: GmProgressBarProps) => {
  const showLight = percent > 0 && showLightEffect;
  const showSteps = currentStep !== undefined && totalSteps !== undefined;

  return (
    <div className={cn('gm-progress-bar-gradient gm-border-progress-bar', gmProgressBarVariant({ appearance }), className)}>
      <div
        data-slot="progressBarFill"
        style={{ width: `${percent}%`, display: percent === 0 ? 'none' : 'block' }}
        className="gm-progress-bar-bg-gradient-external absolute inset-y-0 left-0 rounded-full shadow-[0_0.125rem_0.125rem_0_rgba(28,15,41,0.55)]"
      >
        <div
          data-slot="progressBarTrack"
          style={{ width: 'calc(100% - 0.25rem)', display: percent === 0 ? 'none' : 'block' }}
          className="absolute -translate-y-1/2 top-1/2 left-0.5 rounded-full gm-progress-bar-bg-gradient-internal"
        >
          {
            showLight && (
              <div className="absolute -right-0.5 -top-0.5 size-4">
                <Image
                  aria-hidden
                  draggable={false}
                  src={progressBarLight}
                  alt="Progress bar light effect"
                  className="pointer-events-none absolute left-1/2 top-1/2 size-25.5 max-w-none -translate-x-1/2 -translate-y-1/2 select-none object-contain"
                />
              </div>
            )
          }

        </div>
      </div>
      {
        showSteps && (
          <div className="absolute inset-y-0 right-2 z-20 flex items-center">
            <span className="text-c300 font-semibold text-[10px] leading-[1.4] tracking-normal">
              {currentStep}
              /
              {totalSteps}
            </span>
          </div>
        )
      }
    </div>
  );
};
